/**
 * Sign-in, and keeping the session alive.
 *
 * ═══ Why this needs to exist at all ══════════════════════════════════════════
 *
 * The console read a bearer token out of `localStorage` and nothing ever wrote one. `setToken` was
 * exported and called by nothing, so every visitor landed on the unauthenticated state — the app was
 * unusable by a human regardless of where it was deployed.
 *
 * ═══ The 15-minute problem ═══════════════════════════════════════════════════
 *
 * Access tokens expire after **15 minutes** (ACCESS_TOKEN_TTL_SEC on the server). A console that holds
 * one static token therefore stops working a quarter of an hour after sign-in — mid-task, with no
 * explanation, on a tool somebody keeps open all day. So a session is a PAIR: a short access token and
 * a 30-day refresh token, and this module's real job is the refresh.
 *
 * ═══ Refresh is single-flight, and that is a correctness requirement ══════════
 *
 * `POST /auth/refresh` ROTATES: it consumes the refresh token it was given and returns a new one. The
 * old value is then dead, and the server has a replay limiter watching for reuse.
 *
 * The Overview screen fires four requests in parallel. If the access token has expired, all four come
 * back 401 at once. Without deduplication each would start its own refresh, one would win, and the
 * other three would present a token that has just been consumed — failing, and tripping the replay
 * limiter with what looks exactly like a stolen-token attack. The session would die precisely when it
 * was supposed to be renewed. So concurrent callers share ONE in-flight refresh promise. This is not an
 * optimisation.
 *
 * ═══ Storage, and its honest trade-off ═══════════════════════════════════════
 *
 * Both tokens live in `localStorage`, which means script running on this origin can read them. The
 * alternative — an httpOnly cookie — cannot be used here: `/v1` is deliberately Bearer-only, because a
 * cookie would make every state-changing endpoint CSRF-able from any site the customer visits, and the
 * console lives on its own origin. This is the same model the PulseAssist AI dashboard already uses
 * (`sf_token` / `sf_refresh_token`), so the console is not inventing a weaker one. Own key names,
 * because two products sharing a storage key would sign each other out.
 */

const ACCESS_KEY = "pa-session-token";
const REFRESH_KEY = "pa-refresh-token";
const EXPIRES_KEY = "pa-session-expires-at";
/** Kept only to show who is signed in. Never trusted for anything. */
const IDENTITY_KEY = "pa-session-identity";

const BASE = import.meta.env.VITE_API_ORIGIN ?? "";

/**
 * Refresh this long before the token actually expires.
 *
 * Not zero: a request that leaves with 3 seconds of validity left can arrive after it has lapsed, and
 * the round trip counts against the clock. Sixty seconds is comfortably longer than any request here.
 */
const REFRESH_MARGIN_MS = 60_000;

export interface Identity {
  id: number;
  email: string;
  name: string | null;
  role: string;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode. The session simply will not persist; requests 401 and the UI asks for sign-in.
    return null;
  }
}

function write(entries: Record<string, string | null>): void {
  try {
    for (const [key, value] of Object.entries(entries)) {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    }
  } catch {
    /* private mode — nothing to persist to */
  }
}

export function accessToken(): string | null {
  return read(ACCESS_KEY);
}

export function identity(): Identity | null {
  const raw = read(IDENTITY_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Identity;
  } catch {
    return null;
  }
}

/** True when there is a credential to try. Not a claim that it is still valid. */
export function isSignedIn(): boolean {
  return accessToken() !== null;
}

function expiresAt(): number | null {
  const raw = read(EXPIRES_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}

/** Within the refresh margin, or already past expiry. */
function isNearlyExpired(): boolean {
  const at = expiresAt();
  if (at === null) return false; // Unknown expiry: let the 401 path handle it rather than guessing.
  return Date.now() >= at - REFRESH_MARGIN_MS;
}

interface TokenPair {
  accessToken?: string;
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: { id: number; email: string; name: string | null; role: string };
}

function store(pair: TokenPair): string | null {
  // The server sends the access token as both `accessToken` and `token`; either is authoritative.
  const access = pair.accessToken ?? pair.token ?? null;
  if (!access || !pair.refreshToken) return null;

  write({
    [ACCESS_KEY]: access,
    [REFRESH_KEY]: pair.refreshToken,
    [EXPIRES_KEY]: String(Date.now() + (pair.expiresIn ?? 900) * 1000),
    ...(pair.user
      ? {
          [IDENTITY_KEY]: JSON.stringify({
            id: pair.user.id, email: pair.user.email, name: pair.user.name, role: pair.user.role,
          }),
        }
      : {}),
  });
  return access;
}

export function clearSession(): void {
  write({ [ACCESS_KEY]: null, [REFRESH_KEY]: null, [EXPIRES_KEY]: null, [IDENTITY_KEY]: null });
  notify();
}

// ─── Subscribers, so the shell re-renders on sign-in and sign-out ─────────────

type Listener = () => void;
const listeners = new Set<Listener>();

export function onSessionChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of [...listeners]) fn();
}

// ─── Sign in ─────────────────────────────────────────────────────────────────

export class SignInError extends Error {
  readonly status: number;
  /** The server is asking for a fresh captcha. The form must re-render the widget. */
  readonly captchaRequired: boolean;
  readonly retryAfterSeconds: number | null;
  constructor(status: number, message: string, opts: { captchaRequired?: boolean; retryAfterSeconds?: number | null } = {}) {
    super(message);
    this.name = "SignInError";
    this.status = status;
    this.captchaRequired = opts.captchaRequired ?? false;
    this.retryAfterSeconds = opts.retryAfterSeconds ?? null;
  }
}

export interface CaptchaConfig {
  enabled: boolean;
  provider: string;
  siteKey: string | null;
}

/**
 * Whether this deployment demands a captcha, and the site key to render.
 *
 * Asked rather than assumed. Turnstile is configured in production and absent locally, so a form that
 * hard-codes either answer is broken in one of the two places — and the broken direction is the
 * production one, where every sign-in would be refused with `captchaRequired` and no widget on screen
 * to satisfy it.
 */
export async function fetchCaptchaConfig(): Promise<CaptchaConfig> {
  try {
    const res = await fetch(`${BASE}/api/auth/captcha-config`, { headers: { Accept: "application/json" } });
    if (!res.ok) return { enabled: false, provider: "turnstile", siteKey: null };
    return (await res.json()) as CaptchaConfig;
  } catch {
    /*
     * Treated as "no captcha" on a network failure. The server is the authority: if it does want one it
     * will refuse the sign-in with `captchaRequired`, and the form re-renders the widget then. Guessing
     * "enabled" here would instead block sign-in on a deployment that never wanted a captcha.
     */
    return { enabled: false, provider: "turnstile", siteKey: null };
  }
}

// ─── Federated sign-in (Google) ──────────────────────────────────────────────

export interface SsoConfig {
  google: { enabled: boolean; clientId: string | null };
}

/**
 * Whether this deployment offers "Continue with Google", and the client ID to initialise with.
 *
 * Asked at runtime for the same reason the captcha config is: `import.meta.env.VITE_*` is inlined by
 * Vite at BUILD time, and a value missing from the build environment becomes a silently wrong constant
 * in the shipped bundle. This project has already been bitten by exactly that. Reading it from the
 * server means turning Google sign-in on is an env change and a restart, not a rebuild.
 *
 * Fails to DISABLED, which is the honest direction: without a client ID there is nothing to render a
 * Google button with, and a button that cannot work is worse than no button.
 */
export async function fetchSsoConfig(): Promise<SsoConfig> {
  try {
    const res = await fetch(`${BASE}/api/auth/sso-config`, { headers: { Accept: "application/json" } });
    if (!res.ok) return { google: { enabled: false, clientId: null } };
    return (await res.json()) as SsoConfig;
  } catch {
    return { google: { enabled: false, clientId: null } };
  }
}

/**
 * Exchange a Google `id_token` for a PulseAssist session.
 *
 * `product` is only consulted when the account does not exist yet — the server ignores it for an
 * existing account, because a field on a sign-in request is not a purchase. Sending "email" here means
 * somebody arriving through this console gets a PulseAssist Email subscription and no AI one.
 *
 * Returns whether an account was CREATED, so the caller can say "welcome" rather than "welcome back".
 */
export async function signInWithGoogle(
  credential: string,
  product: "email" | "ai" = "email",
): Promise<{ identity: Identity; created: boolean }> {
  const res = await fetch(`${BASE}/api/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ credential, product }),
  });

  const body = await res.json().catch(() => null as any);

  if (!res.ok) {
    throw new SignInError(res.status, body?.error ?? body?.detail ?? "Google sign-in failed.", {
      retryAfterSeconds: body?.retryAfterSeconds ?? null,
    });
  }

  if (!store(body ?? {})) {
    throw new SignInError(500, "Signed in, but the server did not return a usable session.");
  }
  notify();

  const who = identity();
  if (!who) throw new SignInError(500, "Signed in, but no account details were returned.");
  return { identity: who, created: Boolean(body?.created) };
}

// ─── Standalone registration ─────────────────────────────────────────────────

/**
 * Ask the server to mail a 6-digit code to this address.
 *
 * Registration REFUSES (403 `email_not_verified`) unless this code has been confirmed first, so this is
 * not an optional nicety — it is step one of two. A 409 here means the address already has an account,
 * which is worth surfacing as "sign in instead" rather than as a failure.
 */
export async function sendSignUpCode(email: string): Promise<void> {
  const res = await fetch(`${BASE}/api/auth/send-email-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null as any);
    throw new SignInError(res.status, body?.error ?? "Could not send a verification code.");
  }
}

/** Confirm the mailed code. Its proof is held server-side and consumed by `registerEmailAccount`. */
export async function confirmSignUpCode(email: string, code: string): Promise<void> {
  const res = await fetch(`${BASE}/api/auth/verify-email-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null as any);
    throw new SignInError(res.status, body?.error ?? "That code was not accepted.");
  }
}

/**
 * Create a PulseAssist **Email** account.
 *
 * `product: "email"` is the whole point of this function. Without it the server provisions an AI
 * workspace, an AI free plan and a pot of AI trial tokens — none of which somebody who came here for
 * email infrastructure asked for — and it would NOT create the Email product subscription that every
 * `/v1` endpoint checks for.
 *
 * Must be called after `confirmSignUpCode`, or the server answers 403 `email_not_verified`.
 */
export async function registerEmailAccount(input: {
  email: string;
  password: string;
  name: string;
  companyName?: string;
  captchaToken?: string | null;
}): Promise<Identity> {
  const res = await fetch(`${BASE}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email: input.email,
      password: input.password,
      name: input.name,
      ...(input.companyName ? { companyName: input.companyName } : {}),
      ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
      product: "email",
    }),
  });

  const body = await res.json().catch(() => null as any);
  if (!res.ok) {
    throw new SignInError(res.status, body?.error ?? body?.detail ?? "Could not create your account.", {
      captchaRequired: Boolean(body?.captchaRequired),
    });
  }

  if (!store(body ?? {})) {
    throw new SignInError(500, "Account created, but the server did not return a usable session.");
  }
  notify();
  const who = identity();
  if (!who) throw new SignInError(500, "Account created, but no account details were returned.");
  return who;
}

export async function signIn(email: string, password: string, captchaToken?: string | null): Promise<Identity> {
  const res = await fetch(`${BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password, ...(captchaToken ? { captchaToken } : {}) }),
  });

  const body = await res.json().catch(() => null as any);

  if (!res.ok) {
    /*
     * The server's own wording is used verbatim, and deliberately not improved on. It answers a wrong
     * password and a throttled account with the SAME message so neither confirms that an address is
     * worth attacking, and a friendlier client-side message would undo that.
     */
    throw new SignInError(
      res.status,
      body?.error ?? body?.detail ?? "Sign-in failed.",
      {
        captchaRequired: Boolean(body?.captchaRequired),
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
      },
    );
  }

  if (!store(body ?? {})) {
    throw new SignInError(500, "Signed in, but the server did not return a usable session.");
  }
  notify();

  const who = identity();
  if (!who) throw new SignInError(500, "Signed in, but no account details were returned.");
  return who;
}

export async function signOut(): Promise<void> {
  const refreshToken = read(REFRESH_KEY);
  /*
   * The local session is cleared FIRST, and the server call is best-effort.
   *
   * If revocation fails — offline, server down — the user must still end up signed out of this browser.
   * Leaving them logged in because a network request failed is the wrong way round: the visible
   * intention was to leave.
   */
  clearSession();
  if (!refreshToken) return;
  try {
    await fetch(`${BASE}/api/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    /* already signed out locally */
  }
}

// ─── Refresh ─────────────────────────────────────────────────────────────────

/** The one in-flight refresh. See the note at the top of this file. */
let inFlight: Promise<string | null> | null = null;

/**
 * Exchange the refresh token for a new pair. Returns the new access token, or null.
 *
 * Null means "this session is over" — the caller should show the sign-in screen rather than retrying.
 * On failure the session is CLEARED: a refresh token the server has rejected is not going to start
 * working, and keeping it means every subsequent request performs the same doomed exchange.
 */
export function refreshSession(): Promise<string | null> {
  if (inFlight) return inFlight;

  const refreshToken = read(REFRESH_KEY);
  if (!refreshToken) return Promise.resolve(null);

  inFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clearSession();
        return null;
      }
      const body = await res.json().catch(() => null);
      const access = body ? store(body) : null;
      if (!access) {
        clearSession();
        return null;
      }
      return access;
    } catch {
      /*
       * A NETWORK failure is not a rejected token, so the session is left alone here. Clearing it would
       * sign someone out because their wifi dropped for a second; the next request will try again.
       */
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Called before each API request: refresh proactively if the token is about to lapse.
 *
 * Cheaper and calmer than waiting for the 401 — the request that would have failed simply carries a
 * fresh token instead. The 401 path still exists, because the clock can be wrong, the token can be
 * revoked server-side, and `expiresIn` can be missing.
 */
export async function ensureFreshToken(): Promise<void> {
  if (isSignedIn() && isNearlyExpired()) await refreshSession();
}
